<template>
  <div class="meeting-item" @click="openMeeting($event)">
    <div class="meeting-content">
      <span v-if="emoji" class="emoji">{{ emoji }}</span>
      <EventIcon v-else class="event-icon" />
      <span class="meeting-title">{{ title || "Untitled" }}</span>
      <div class="meeting-meta">
        <div
          v-if="meeting.calendarColor"
          class="calendar-bar"
          :class="[`option-color-${meeting.calendarColor}`, { past: isPast }]"
        />
        <span class="meeting-date">{{ dateLabel }}</span>
        <span v-if="timeLabel" class="meeting-separator">·</span>
        <span v-if="timeLabel" class="meeting-time">{{ timeLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { format, getYear } from "date-fns";
import { computed, defineComponent, PropType, toRefs, watch } from "vue";

import EventIcon from "@/components/icons/EventIcon.vue";
import * as inject from "@/components/inject";
import type { Meeting } from "@/components/meetings/meetingGroups";
import { useEmoji } from "@/composables/useEmoji";
import { useReleaser } from "@/ref";
import { navigateToObject } from "@/utils/navigate";

export default defineComponent({
  name: "MeetingItem",
  components: {
    EventIcon,
  },
  props: {
    meeting: {
      type: Object as PropType<Meeting>,
      required: true,
    },
  },
  setup(props) {
    const router = inject.router();
    const controller = inject.controller();
    const clock = inject.clock();
    const releaser = useReleaser();
    const { meeting } = toRefs(props);
    const { title, emoji, updateText } = useEmoji(meeting.value.title);
    releaser.defer(watch(() => meeting.value.title, updateText));

    const dateLabel = computed(() => {
      const start = meeting.value.start;
      return getYear(start) !== getYear(clock.value)
        ? format(start, "MMM d, yyyy")
        : format(start, "EEE, MMM d");
    });

    const isPast = computed(() => meeting.value.end < clock.value);

    const timeLabel = computed(() => {
      if (meeting.value.allDay) return null;
      return `${format(meeting.value.start, "p")} → ${format(
        meeting.value.end,
        "p"
      )}`;
    });

    const openMeeting = (mouseEvent?: MouseEvent) => {
      void navigateToObject(
        router,
        controller,
        { kind: "event", id: meeting.value.id },
        mouseEvent
      );
    };

    return {
      dateLabel,
      emoji,
      isPast,
      openMeeting,
      timeLabel,
      title,
    };
  },
});
</script>

<style scoped lang="scss">
.meeting-item {
  padding-block: 8px;
  padding-inline: 16px;
  border-radius: $border-radius-m;
  box-sizing: border-box;
  cursor: default;
  @include transition(background-color 100ms ease-out);

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }

  &:active {
    @include themed() {
      background-color: t($grey-10);
    }
  }

  .meeting-content {
    display: flex;
    align-items: center;
    gap: 8px;

    // The right-aligned block: calendar bar, date, then times.
    .meeting-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
      margin-inline-start: auto;
    }

    // Same bar as the agenda's event calendar border, including the dimmed
    // rendering of past events.
    .calendar-bar {
      flex-shrink: 0;
      width: 2px;
      height: 20px;
      border-radius: 10px;
      @include generate-calendar-mid-colors;

      &.past {
        opacity: 0.3;
      }
    }

    .emoji {
      flex-shrink: 0;
      width: 16px;
      text-align: center;
    }

    .event-icon {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
    }

    .meeting-title {
      @extend %font-body-medium;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      @include themed() {
        color: t($primary);
      }
    }

    .meeting-date {
      @extend %font-caption-regular;
      flex-shrink: 0;
      @include themed() {
        color: t($grey-50);
      }
    }

    .meeting-separator {
      @extend %font-caption-regular;
      flex-shrink: 0;
      @include themed() {
        color: t($grey-50);
      }
    }

    .meeting-time {
      @extend %font-caption-regular;
      flex-shrink: 0;
      @include themed() {
        color: t($grey-50);
      }
    }
  }
}
</style>
